import { MutableRefObject, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import classNames from "classnames";
import { NestedOrganizationProps } from "../../../core/api/organizations/organizations";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import { Row, Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import Button from "../../../ui-lib/components/Button/Button";
import useTranslations from "../../../core/i18n/useTranslations";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";
import {
  IADGroupRequest,
  ITemplatesItemsProps,
} from "../../../core/api/adGroup/types";
import { IRoleTemplateDropdown } from "../../../core/api/organizations/types";
import { HierarchyUsage } from "../../../core/api/roleTemplates/types";
import useUser from "../../../core/user/useUser";
import { getResponseCenter } from "../../../core/api/responsecenters/responsecenters";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import { getFlatOrganizations } from "../../../ui-lib/utils/organizationsHelper";
import { notify } from "../../../ui-lib/components/Alerts/Toast";

import styles from "../Organizations.module.css";

export const RoleTemplateRow = ({
  index,
  generalTemplatesData,
  organizationsData,
  arcDataCache,
  loadedArcTemplates,
  templateError,
  onTemplateChange,
  onRemove,
}: {
  index: number;
  generalTemplatesData: ITemplatesItemsProps[];
  organizationsData: NestedOrganizationProps[];
  arcDataCache: MutableRefObject<Map<number, IItemProp>>;
  loadedArcTemplates: MutableRefObject<Set<number>>;
  templateError?: string;
  onTemplateChange?: () => void;
  onRemove?: () => void;
}) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();

  const [filteredData, setFilteredData] = useState<IItemProp[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [roleTemplatesData, setRoleTemplatesData] =
    useState(generalTemplatesData);
  const [isRespondentTemplate, setIsRespondentTemplate] = useState<boolean>();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IADGroupRequest>();

  // Watch the current role template values
  const templateId = watch(`roleTemplates.${index}.templateId`);
  const organizationId = watch(`roleTemplates.${index}.organizationId`);
  const responseCenterId = watch(`roleTemplates.${index}.responseCenterId`);

  // Watch all role templates to get the count
  const allRoleTemplates = watch("roleTemplates");
  const templateQuantity = allRoleTemplates?.length ?? 0;

  const showNoItemsToShow = () => {
    notify({
      variant: "error",
      message: t("Organizations:no_items_in_template"),
    });
  };

  const filterOrganizations = (
    idToCheck?: number,
    hierarchy?: HierarchyUsage,
    data = organizationsData,
    saveData = true,
  ) => {
    data.forEach((org) => {
      if (org.item.id === idToCheck) {
        let levelToCheck: number;
        switch (hierarchy) {
          case HierarchyUsage.OneBellow:
            levelToCheck = 1;
            break;
          case HierarchyUsage.ThisOrganization:
            levelToCheck = 0;
            break;
          case HierarchyUsage.AllBellow:
          default:
            levelToCheck = Infinity;
            break;
        }
        const flatOrganizationsArray = getFlatOrganizations(
          [org],
          [],
          levelToCheck,
        );
        if (flatOrganizationsArray.length === 0) {
          showNoItemsToShow();
        }
        if (saveData) {
          setFilteredData(
            flatOrganizationsArray.map((flatOrg) => ({
              id: flatOrg.id,
              name: flatOrg.name,
              isSelected: organizationId === flatOrg.id,
              type: "customer",
            })),
          );
        }
      } else {
        filterOrganizations(idToCheck, hierarchy, org.children);
      }
    });
  };

  const uploadAlarmReceptions = async (idToCheck: number) => {
    // get cached data
    const cached = arcDataCache.current.get(idToCheck);

    // if cache exists, use it and don't make API call
    if (cached) {
      setFilteredData([cached]);
      setIsRespondentTemplate(true);
      setValue(`roleTemplates.${index}.responseCenterId`, Number(cached.id));
      return;
    }
    // if arc id already loaded, don't load again
    if (loadedArcTemplates.current.has(idToCheck)) return;
    // add arc id to loaded set
    loadedArcTemplates.current.add(idToCheck);
    try {
      setIsLoading(true);
      const receptionData = await getResponseCenter(
        idToCheck,
        authenticatedRequest,
      );
      const dataToSet = {
        id: receptionData.data.id,
        name: receptionData.data.displayName,
        isSelected: true,
        type: "respondent",
      } as IItemProp;
      // cache arc the data
      arcDataCache.current?.set(idToCheck, dataToSet);
      setFilteredData([dataToSet]);
      setIsRespondentTemplate(true);
      setValue(
        `roleTemplates.${index}.responseCenterId`,
        receptionData.data.id,
      );
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  const findTemplate = (id: number) =>
    roleTemplatesData.find((template) => template.id === id);

  useEffect(() => {
    if (templateId && templateId > 0) {
      const updatedTemplates = roleTemplatesData.map((template) => ({
        ...template,
        isSelected: template.id === templateId,
      }));
      setRoleTemplatesData(updatedTemplates);

      const usedTemplate = findTemplate(templateId);
      if (!usedTemplate) return;
      if (organizationId) {
        filterOrganizations(usedTemplate.orgId, usedTemplate.hierarchy);
        return;
      }
      if (responseCenterId) {
        uploadAlarmReceptions(usedTemplate.arcId);
        return;
      }
    }
  }, [templateId, organizationId, responseCenterId, arcDataCache.current.size]);

  const selectedItem = useMemo(
    () =>
      filteredData?.find(
        (org) =>
          org.id === (isRespondentTemplate ? responseCenterId : organizationId),
      ),
    [filteredData, responseCenterId, organizationId, isRespondentTemplate],
  );

  return (
    <Row align="start" type="left">
      <TwoColGrid className={styles.AdRoleTemplateGrid}>
        <Controller
          name={`roleTemplates.${index}.templateId`}
          control={control}
          rules={{
            required: t("Errors:input_field_required"),
          }}
          render={({ field }) => (
            <Dropdown
              withFilter
              maxVisible={10}
              title={`${index === 0 ? t("Common:role_template") : ""}`}
              items={roleTemplatesData}
              selectedItem={roleTemplatesData?.find(
                (template) => template.id === field.value,
              )}
              onSelectItem={async (item: IRoleTemplateDropdown) => {
                field.onChange(item.id);
                onTemplateChange?.();
                const updatedRoleTemplates = roleTemplatesData.map(
                  (template) => ({
                    ...template,
                    isSelected: item.id === template.id,
                  }),
                );
                setRoleTemplatesData(updatedRoleTemplates);
                if (item.type === "customer") {
                  // Clear responseCenterId when switching to customer template
                  setValue(
                    `roleTemplates.${index}.responseCenterId`,
                    undefined,
                  );
                  setIsRespondentTemplate(false);
                  filterOrganizations(item.orgId, item.hierarchy);
                } else {
                  // Clear organizationId when switching to respondent template
                  setValue(`roleTemplates.${index}.organizationId`, undefined);
                  await uploadAlarmReceptions(item.arcId);
                }
              }}
              validationError={
                templateError ||
                errors.roleTemplates?.[index]?.templateId?.message
              }
            />
          )}
        />

        {isRespondentTemplate ? (
          <Controller
            name={`roleTemplates.${index}.responseCenterId`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Dropdown
                withFilter
                maxVisible={10}
                title={`${index === 0 ? t("Organizations:applies_to") : ""}`}
                searchPlaceholder={t("Common:search_placeholder")}
                isLoading={isLoading}
                disabled={
                  isLoading || !filteredData?.length || isRespondentTemplate
                }
                selectedItem={selectedItem}
                items={filteredData || []}
                onSelectItem={(item: IRoleTemplateDropdown) => {
                  if (filteredData) {
                    const updatedOrgs = [...filteredData].map((org) => ({
                      ...org,
                      isSelected: item.id === org.id,
                    }));
                    setFilteredData(updatedOrgs);
                    field.onChange(item.id);
                  }
                }}
              />
            )}
          />
        ) : (
          <Controller
            name={`roleTemplates.${index}.organizationId`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Dropdown
                withFilter
                maxVisible={10}
                title={`${index === 0 ? t("Organizations:applies_to") : ""}`}
                searchPlaceholder={t("Common:search_placeholder")}
                isLoading={isLoading}
                disabled={isLoading || !filteredData?.length}
                selectedItem={selectedItem}
                items={filteredData || []}
                onSelectItem={(item: IRoleTemplateDropdown) => {
                  if (filteredData) {
                    const updatedOrgs = [...filteredData].map((org) => ({
                      ...org,
                      isSelected: item.id === org.id,
                    }));
                    setFilteredData(updatedOrgs);
                    field.onChange(item.id);
                  }
                }}
                validationError={
                  errors.roleTemplates?.[index]?.organizationId &&
                  t("Errors:input_field_required")
                }
              />
            )}
          />
        )}
      </TwoColGrid>

      <Spacer size={32} />

      {templateQuantity > 1 && (
        <Button
          image="x"
          variant="secondary"
          onClick={onRemove}
          className={classNames(styles.AdRemoveRoleButton, {
            ["mt-24"]: index === 0,
          })}
        />
      )}
    </Row>
  );
};
